import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'next-i18next';
import Tooltip from 'react-tooltip-lite';
import {
  formatNumericValue,
  nameIdPairValidationSchema,
  truncateString,
} from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';
import * as yup from 'yup';

import CreateEditInput from '@/modules/settings/company/components/Inputs/Modals/CreateEditInput';
import SupplierActions from '@/modules/settings/company/components/Suppliers/Actions';
import { ICompanySettings } from '@/modules/settings/company/types';
import { NextImage, Typography } from '@/shared/components/atoms';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { EntityType } from '@/shared/constant/enums';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { getCountryFullName, getCountryIcon } from '@/shared/lib/country-flags';
import { getLocalizedDate } from '@/shared/lib/dates';
import { validatePhoneNumber } from '@/shared/lib/libphonenumber';

import Edit from '~/images/shared/edit.svg';
import InfoCircle from '~/images/shared/ExleminationMark.svg';

export const useCompanySettingsUtils = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { openModal } = useModal();

  //***************** View and edit general company data *******************/

  const getCompanyBasicInfo = (
    data: ICompanySettings.ICompanyDetails
  ): ICompanySettings.ICompanyBasicInfo => {
    return {
      name: data.name,
      companyIndustry: data.companyIndustry,
      areaUnit: data.areaUnit,
      totalArea: data.totalArea,
      website: data.website,
    };
  };

  const validationSchema: yup.SchemaOf<
    Omit<ICompanySettings.ICompanyDetailsForm, 'id'>
  > = yup.object().shape({
    name: yup
      .string()
      .required(t('this-field-is-required'))
      .max(
        100,
        t('this-field-not-accept-more-than-x-character', { count: 100 })
      ),
    companyIndustry: yup.string().required(t('this-field-is-required')),
    totalArea: yup
      .number()
      .min(1, t('non-zero-field'))
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .defined(),
    logo: yup.mixed().notRequired(),
    areaUnit: yup.string().required(),
    website: yup
      .string()
      .url(t('Please enter a valid website.'))
      .nullable()
      .defined(),
    location: yup.string().nullable().defined(),
    country: yup.string().required(),
    isFacialRecognitionActive: yup
      .boolean()
      .required(t('this-field-is-required')),
  });

  const getFormData = (
    data: ICompanySettings.ICompanyDetailsForm,
    hasFacial: boolean
  ): FormData => {
    const formData: FormData = new FormData();

    formData.append('name', data.name);

    formData.append('country', data.country);

    if (data.areaUnit) formData.append('areaUnit', data.areaUnit);
    formData.append(
      'location',
      data.location && data.location.length ? data.location : '-'
    );

    formData.append(
      'website',
      data.website && data.website.length > 0
        ? data.website
        : 'https://www.example.com'
    );

    if (typeof data.logo !== 'string' && data?.logo?.[0]?.file)
      formData.append('logo', data.logo[0].file);

    formData.append('totalArea', data.totalArea ? String(data.totalArea) : '0');

    if (hasFacial) {
      formData.append(
        'isFacialRecognitionActive',
        String(data.isFacialRecognitionActive)
      );
    }

    return formData;
  };

  //***************** Properties *******************/

  const getPropertiesTable = () => {
    const columnHelper = createColumnHelper<ICompanySettings.IProperty>();
    return [
      columnHelper.accessor('name', {
        header: t('name'),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('entityTypeId', {
        header: t('the-type'),
        cell: ({ getValue }) =>
          getValue() === EntityType.FARM ? t('farm') : t('packhouse'),
      }),
      columnHelper.accessor('country', {
        header: t('country'),
        cell: ({ getValue }) => (
          <div className='flex items-center gap-8'>
            <NextImage
              src={getCountryIcon(getValue())}
              width={24}
              height={16}
              alt='country flag'
            />
            {getCountryFullName(getValue())}
          </div>
        ),
      }),
      columnHelper.accessor('totalArea', {
        header: t('area'),
        //  TODO: make sure the area is always acres
        cell: ({ getValue, row: { original } }) => (
          <div className='text-surface-10'>
            <Typography>
              {getValue()} {t(original.areaUnit ?? '')}
            </Typography>
            <Typography variant='subtitle2'>
              {`(${original.fieldCount} ${t('Active Fields')})`}
            </Typography>
          </div>
        ),
      }),
      columnHelper.accessor('crops', {
        header: t('crops'),
        cell: ({ getValue }) =>
          getValue()?.length ? (
            <PartialListWithTooltip
              limit={2}
              items={getValue().map((item) => ({
                description: item.name,
                imageURL: item.image,
              }))}
            />
          ) : (
            '-'
          ),
      }),
    ];
  };

  const addPropertyValidationSchema: yup.SchemaOf<ICompanySettings.IAddPropertyFormDTO> =
    yup.object({
      name: yup.string().required(t('this-field-is-required')),
      type: yup.string().required(t('this-field-is-required')),
      totalArea: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .notRequired(),
      areaUnit: yup.string().notRequired(),
      location: yup.string().notRequired(),
      // crops: yup.array().of(cropSchema).min(1),
      companyId: yup.number().required(),
      country: yup.string().required(),
      currency: yup.string().nullable().notRequired(),
      timeZone: yup.string().notRequired(),
    });

  //***************** Company Suppliers *******************/

  const getSuppliersTable = () => {
    const columnHelper = createColumnHelper<ICompanySettings.ISupplier>();

    return [
      columnHelper.accessor('name', {
        header: t('Supplier Name'),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('phoneNumber', {
        header: t('Supplier Phone Number'),
        cell: ({ getValue }) => getValue() || '-',
      }),
      columnHelper.accessor('communicationOfficer', {
        header: t('Contact Person'),
        cell: ({ getValue }) => getValue() || '-',
      }),
      columnHelper.accessor('contactOfficerNumber', {
        header: t('Contact Person Number'),
        cell: ({ getValue }) => getValue() || '-',
      }),
      columnHelper.display({
        id: 'supplierActions',
        cell: ({ row: { original } }) => (
          <div className='flex justify-end'>
            <SupplierActions supplier={original} />
          </div>
        ),
      }),
    ];
  };

  const getArchivedSuppliersTable = () => {
    const columnHelper =
      createColumnHelper<ICompanySettings.IArchivedSupplier>();
    const supplierTable = getSuppliersTable();
    return [
      ...supplierTable.slice(0, supplierTable.length - 1),
      columnHelper.accessor('deactivatedByName', {
        header: t('deactivated-by'),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('deactivatedAt', {
        header: t('deactivation-time'),
        cell: ({ getValue }) =>
          getLocalizedDate({
            date: getValue(),
            formatDate: 'dd/MM/yyyy, h:mm a',
            locale: language,
          }),
      }),
    ];
  };

  const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9\p{L}\p{N}\s]+$/u;
  const supplierValidationSchema = yup.object().shape(
    {
      name: yup
        .string()
        .required(t('this-field-is-required'))
        .max(
          100,
          t('this-field-not-accept-more-than-x-character', { count: 100 })
        )
        .matches(
          //  INFO: https://dev.to/tillsanders/let-s-stop-using-a-za-z-4a0m
          ALPHANUMERIC_REGEX,
          t('this-field-is-char-and-nums-only')
        ),
      phoneNumber: yup
        .string()
        .nullable()
        .test('isValidPhoneNumber', t('wrong-phone-number'), (value) =>
          validatePhoneNumber(value)
        ),
      communicationOfficer: yup
        .string()
        .nullable()
        .when('communicationOfficer', {
          is: (value: string | null | undefined) => value?.length,
          then: (rule) =>
            rule
              .matches(
                ALPHANUMERIC_REGEX,
                t('this-field-is-char-and-nums-only')
              )
              .max(
                100,
                t('this-field-not-accept-more-than-x-character', {
                  count: 100,
                })
              ),
        }),
      contactOfficerNumber: yup
        .string()
        .nullable()
        .test('isValidPhoneNumber', t('wrong-phone-number'), (value) =>
          validatePhoneNumber(value)
        ),
    },
    //INFO: https://stackoverflow.com/questions/66795388/yup-validation-for-a-non-required-field
    [['communicationOfficer', 'communicationOfficer']]
  );

  //***************** custom inputs *******************/

  const openInputsModal = (input?: ICompanySettings.ICompanyInput) =>
    openModal(<CreateEditInput input={input} />, {
      variant: 'full',
      onClose: () =>
        openModal(
          <ConfirmationModal
            header={t('going-somewhere-else')}
            message={t('data-loss-warning')}
            variant='warning'
            dismissText={t('continue-editing')}
            confirmText={t('discard-and-exit')}
            onConfirmRemoveNumber={2}
            buttonsAlignment='col'
            preferredAction='dismiss'
          />
        ),
    });

  const createInputValidationSchema: yup.SchemaOf<ICompanySettings.ICreateInputFormValues> =
    yup.object().shape({
      categoryId: yup.number().required(t('this-field-is-required')),
      countryId: yup.number().required(t('this-field-is-required')),
      manufacturer: yup
        .string()
        .required(t('this-field-is-required'))
        .max(
          100,
          t('this-field-not-accept-more-than-x-character', { count: 100 })
        ),
      measurementType: yup
        .mixed()
        .oneOf(['WEIGHT', 'VOLUME', 'SEEDS'])
        .required(t('this-field-is-required')),
      name: yup
        .string()
        .required(t('this-field-is-required'))
        .max(
          100,
          t('this-field-not-accept-more-than-x-character', { count: 100 })
        ),
      license: yup
        .string()
        .notRequired()
        .max(
          100,
          t('this-field-not-accept-more-than-x-character', { count: 100 })
        ),
      causeIds: yup.array().of(yup.number()).notRequired(),
      hasActiveSubstances: yup.boolean().required(),
      activeSubstances: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.number().nullable().required(t('this-field-is-required')),
            concentration: yup
              .number()
              .nullable()
              .notRequired()
              .max(100, `${t('maximum')} 100`),
          })
        )
        .notRequired(),
    });

  const inputFilterValidationSchema: yup.SchemaOf<ICompanySettings.ICompanyInputFiltersForm> =
    yup.object().shape({
      country: yup
        .object()
        .shape({
          name: yup.string().optional(),
          id: yup.number().optional(),
        })
        .optional(),
      activeSubstances: yup
        .array()
        .of(nameIdPairValidationSchema(t))
        .optional(),
      categories: yup.array().of(nameIdPairValidationSchema(t)).optional(),
      measurementTypes: yup.array().of(yup.string()).optional(),
    });

  const getInputsTable = (inputType: ICompanySettings.InputType) => {
    const columnHelper = createColumnHelper<ICompanySettings.ICompanyInput>();
    return [
      columnHelper.accessor('name', {
        header: t('name'),
        cell: ({ getValue }) => (
          <Typography className='flex items-center gap-4'>
            {truncateString({
              text: getValue(),
              length: 45,
            })}
            {inputType === 'custom' && (
              <Tooltip
                content={
                  <Typography variant='caption' className='p-2'>
                    {t('custom-created-input')}
                  </Typography>
                }
                background={themeColors().background['neutral-99']}
                className='react-tooltip-rounded'
                arrow
                arrowSize={10}
                direction='bottom'
                tagName='div'
                tipContentHover
                padding={0}
              >
                <span className='cursor-pointer text-surface-10'>
                  <InfoCircle width='16' height='16' />
                </span>
              </Tooltip>
            )}
          </Typography>
        ),
      }),
      columnHelper.accessor('category', {
        header: t('the-type'),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('activeSubstances', {
        header: t('active-substance'),
        cell: ({ getValue }) => {
          const val = getValue();
          return val ? (
            <PartialListWithTooltip
              limit={1}
              items={val.map((item) => ({
                description: `${
                  item.concentration
                    ? `${formatNumericValue(item.concentration, {
                        isPercentage: true,
                      })} `
                    : ''
                }${item.activeSubstanceName}`,
              }))}
            />
          ) : (
            '-'
          );
        },
      }),
      columnHelper.accessor('manufacturer', {
        header: t('manufacturer'),
        cell: ({ getValue }) =>
          truncateString({
            text: getValue(),
            length: 45,
          }),
      }),
      columnHelper.accessor('measurementType', {
        header: t('measurement-unit'),
        cell: ({ getValue }) => t(getValue().toLowerCase()),
      }),
      ...(inputType === 'custom'
        ? [
            columnHelper.display({
              id: 'editInput',
              cell: ({ row: { original } }) => (
                <div className='flex items-center gap-16 text-primary-default'>
                  <span
                    className='cursor-pointer'
                    onClick={() => openInputsModal(original)}
                  >
                    <Edit width='24' height='24' />
                  </span>
                </div>
              ),
            }),
          ]
        : []),
    ];
  };

  return {
    getCompanyBasicInfo,
    addPropertyValidationSchema,
    getPropertiesTable,
    getFormData,
    validationSchema,
    getSuppliersTable,
    getArchivedSuppliersTable,
    supplierValidationSchema,
    createInputValidationSchema,
    getInputsTable,
    openInputsModal,
    inputFilterValidationSchema,
  };
};
